import React, { useEffect } from "react"
import { useAnalytics } from "@app/hooks/useAnalytics"
import { useAppContext } from "@app/providers/app"
import { useShopifyProduct } from "@app/hooks/useShopifyProduct"
import { useMaintenance } from "@app/hooks/useMaintenance"

import type { Location } from "@root/types/global"
import { useInsideDataPage, useInsideDataUser } from "@app/hooks/useInsideData"
import { useBraze } from "@app/hooks/useBraze"
import { useCore } from "@app/hooks/useCore"
import { useCustomerContext } from "@app/providers/customer"
import "focus-visible/dist/focus-visible"

export const withLayout =
  (Component: React.FC<{ data: any; serverData?: any; location: Location; children?: React.ReactNode }>) =>
  ({ name = "Layout", location, children, data, serverData }: any) => {
    const { active, authorised } = useMaintenance(location)
    const { selectProduct } = useShopifyProduct()
    const { trackPageView, tracked } = useAnalytics()
    const { dispatch } = useAppContext()
    const { identify } = useBraze()
    const {
      helpers: { decodeShopifyId },
    } = useCore()
    const { customer } = useCustomerContext()

    useInsideDataUser()
    useInsideDataPage(
      data?.page?.title || serverData?.title || data?.collection?.title || data?.product?.title || "other",
      data?.page?._type || serverData?._type || data?.collection?._type || data?.product?._type || "other"
    )

    useEffect(() => {
      if (tracked) {
        trackPageView()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tracked])

    useEffect(() => {
      selectProduct(data?.product, location?.pathname)
      trackPageView()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location?.pathname])

    useEffect(() => {
      dispatch({ type: "initial" })
    }, [location?.pathname, dispatch])

    useEffect(() => {
      if (!customer?.id) return
      const numericId = decodeShopifyId(customer.id, "Customer")
      if (!numericId) return
      identify(numericId)
    }, [customer?.id, decodeShopifyId, identify])

    Component.displayName = name
    return active ? (
      <>{children}</>
    ) : (
      authorised && (
        <Component data={data} location={location}>
          {children}
        </Component>
      )
    )
  }
